import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Target, TrendUp, Brain, Warning, Scales, ArrowRight, CheckCircle, XCircle, BookOpen, ChartBar, Lightning, Database } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const QuantosTradesValidar = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Quantos Trades para Validar Estratégia | Guia Completo',
      description: 'Descubra quantos trades são necessários para validar uma estratégia e evitar decisões baseadas em poucos dados.',
      path: '/pt/quantos-trades-para-validar-estrategia',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/quantos-trades-para-validar-estrategia'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Validação de Estratégia</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-blue-400 text-sm font-medium mb-4">
          <Database size={16} weight="bold" />
          <span>Validação de Estratégia</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
          Quantos Trades São Necessários para Validar uma Estratégia?
        </h1>

        <p className="text-lg text-gray-400 mb-12">
          A resposta que pode salvar sua conta de decisões precipitadas
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Você testa um novo setup. Faz 5 trades, 4 dão certo. <em className="text-white">"Encontrei o Santo Graal!"</em> Aumenta o tamanho da posição, opera com mais frequência. E nas próximas 20 operações, perde tudo o que ganhou e mais um pouco.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            Esse cenário é mais comum do que parece. <strong className="text-white">Muitos traders tiram conclusões com poucos trades</strong>, confundem sorte com edge, e tomam decisões que destroem suas contas. A pergunta certa não é "esse setup funciona?" — é <strong className="text-white">"quantos trades eu preciso para saber se funciona?"</strong>
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Traders Avaliam Estratégias Errado</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A maioria dos traders comete erros básicos ao avaliar se uma estratégia funciona. E esses erros custam caro.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Poucos Trades</h3>
                </div>
                <p className="text-gray-400 text-sm">5 trades não significam nada. 10 também não. Você pode acertar 8 de 10 por pura sorte e achar que tem um sistema vencedor.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Emoção Após Wins/Losses</h3>
                </div>
                <p className="text-gray-400 text-sm">Uma sequência de 3 gains e você acha que dominou o mercado. Uma sequência de 3 losses e você abandona a estratégia. Emoção não é critério de avaliação.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Decisões Precipitadas</h3>
                </div>
                <p className="text-gray-400 text-sm">Trocar de estratégia toda semana porque "não está funcionando" impede qualquer validação real. Você nunca dá tempo suficiente para os dados falarem.</p>
              </div>
            </div>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartBar size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">A Importância da Amostra</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Trading é um jogo de probabilidades. E probabilidades só fazem sentido com amostras grandes o suficiente.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">Conceito de Amostragem:</h3>
              <p className="text-gray-300 mb-4">
                Se você jogar uma moeda 10 vezes, pode dar 7 caras e 3 coroas. Isso não significa que a moeda é viciada — significa que 10 é uma amostra pequena demais. Com 1000 lançamentos, a proporção se aproxima de 50/50.
              </p>
              <p className="text-gray-400 text-sm">
                Trading funciona igual. Poucos trades = muito ruído. Muitos trades = sinal mais claro.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-red-400 mb-2">Alta</p>
                <p className="text-sm text-gray-500">Aleatoriedade com poucos trades</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-amber-400 mb-2">Média</p>
                <p className="text-sm text-gray-500">Padrões começam a aparecer</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-[#00FF85] mb-2">Baixa</p>
                <p className="text-sm text-gray-500">Mais dados = mais confiança</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Quanto mais trades você tem, mais o ruído estatístico diminui e o verdadeiro desempenho da estratégia aparece.
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Quantos Trades São Necessários</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Não existe um número mágico que funciona para todos. Mas existem referências úteis:
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-amber-400">20–30 trades</h3>
                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">Sinal Inicial</span>
                </div>
                <p className="text-gray-400 text-sm">Você começa a ter uma noção da estratégia, mas ainda é muito cedo para conclusões. Use para identificar problemas óbvios, não para validar.</p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-blue-400">50–100 trades</h3>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Mais Confiável</span>
                </div>
                <p className="text-gray-400 text-sm">Padrões começam a emergir. Win rate, RR médio e profit factor começam a fazer sentido. Você pode começar a tirar conclusões mais sólidas.</p>
              </div>

              <div className="bg-[#00FF85]/10 border border-[#00FF85]/30 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#00FF85]">100+ trades</h3>
                  <span className="text-xs bg-[#00FF85]/20 text-[#00FF85] px-2 py-1 rounded">Alta Confiança</span>
                </div>
                <p className="text-gray-400 text-sm">Agora você tem dados estatisticamente significativos. Se a estratégia é lucrativa com 100+ trades, você pode confiar que tem um edge real.</p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Warning size={20} className="text-amber-400 mt-1 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  <strong className="text-amber-400">Contexto importa:</strong> Se você opera 1 vez por semana, 100 trades podem levar 2 anos. Se opera 5 vezes por dia, pode chegar em 1 mês. Adapte a expectativa à sua realidade.
                </p>
              </div>
            </div>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Influência do Mercado</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O mesmo setup pode ter resultados completamente diferentes dependendo do contexto de mercado.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-3">Mercado em Tendência</h3>
                <p className="text-gray-400 text-sm mb-3">Setups de continuação funcionam bem. Pullbacks são comprados. Momentum favorece quem segue a direção.</p>
                <p className="text-xs text-gray-500">Se você validou durante tendência, não significa que funciona em lateralidade.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-3">Mercado Lateral</h3>
                <p className="text-gray-400 text-sm mb-3">Rompimentos falsos são comuns. Reversões funcionam melhor. O que funcionava em tendência pode sangrar.</p>
                <p className="text-xs text-gray-500">Validação em lateralidade não garante resultado em tendência.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Idealmente, sua validação deve incluir <strong className="text-white">diferentes condições de mercado</strong>. Uma estratégia que só funciona em bull market não é uma estratégia — é sorte.
            </p>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Problema de Misturar Ativos</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um erro comum é validar uma estratégia misturando diferentes ativos e achar que o resultado vale para todos.
            </p>

            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-red-400 mb-4">Por que misturar é problemático:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">BTC ≠ Altcoins:</strong> Bitcoin tem liquidez e comportamento diferentes de uma altcoin de baixo cap. O que funciona em um pode falhar no outro.</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Liquidez diferente:</strong> Ativos menos líquidos têm slippage maior, spreads maiores. Isso afeta diretamente seu RR real.</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Conclusões erradas:</strong> Você pode ter um edge em EURUSD e achar que vale para todos os pares. Não vale.</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Valide separadamente para cada ativo ou categoria de ativos. É mais trabalho, mas evita conclusões falsas.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <ChartLine size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Backtest vs Execução Real</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Backtesting (testar no histórico) é útil, mas não substitui trades reais.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-3">Vantagens do Backtest</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>• Rápido: pode testar anos em horas</li>
                  <li>• Sem risco financeiro</li>
                  <li>• Permite identificar problemas óbvios</li>
                  <li>• Útil para filtrar ideias ruins</li>
                </ul>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-3">Limitações do Backtest</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>• Não considera slippage real</li>
                  <li>• Ignora emoção da execução</li>
                  <li>• Você sabe o que vem depois</li>
                  <li>• Fácil cair em overfitting</li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <p className="text-gray-300">
                <strong className="text-amber-400">A realidade:</strong> Uma estratégia que funciona lindamente no backtest pode fracassar na execução real porque você não consegue ser tão disciplinado quanto um algoritmo olhando o passado.
              </p>
            </div>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Psicologia na Validação</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A mente humana não é feita para avaliar probabilidades com paciência. E isso sabota a validação de estratégias.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Parar Cedo Demais</h3>
                <p className="text-gray-400 text-sm">Uma sequência de 4-5 losses e você abandona a estratégia, mesmo que ela seja lucrativa no longo prazo. A variância natural é confundida com falha do sistema.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Confiar Cedo Demais</h3>
                <p className="text-gray-400 text-sm">Uma sequência de 5-6 wins e você acha que dominou o mercado. Aumenta tamanho, relaxa regras. E quando a variância volta, o estrago é maior.</p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <BookOpen size={28} className="text-[#00FF85] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">"Trading in the Zone" — Mark Douglas</h3>
                  <p className="text-gray-400 text-sm">
                    Douglas explica que você precisa pensar em <strong className="text-white">séries de trades</strong>, não em trades individuais. Cada trade é único e independente. O resultado de um não prevê o próximo. Aceitar isso é fundamental para validar qualquer estratégia.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartBar size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">O Que Acompanhar Durante a Validação</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Não basta contar trades. Você precisa acompanhar as métricas certas.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Win Rate</h3>
                <p className="text-gray-400 text-sm">Percentual de acertos. Importante, mas não isoladamente. Combine sempre com RR.</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Risk Reward</h3>
                <p className="text-gray-400 text-sm">Relação entre ganho médio e perda média. Um RR alto compensa win rate baixo.</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Profit Factor</h3>
                <p className="text-gray-400 text-sm">Lucro total / prejuízo total. Acima de 1.5 é bom, acima de 2.0 é excelente.</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Consistência</h3>
                <p className="text-gray-400 text-sm">A curva de capital é suave ou volátil? Consistência indica robustez do sistema.</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 sm:col-span-2">
                <h3 className="font-semibold text-blue-400 mb-2">Execução</h3>
                <p className="text-gray-400 text-sm">Você está seguindo as regras? Entradas, saídas, stops — tudo conforme planejado? Execução ruim invalida qualquer validação.</p>
              </div>
            </div>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Objetivo Real da Validação</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Validar uma estratégia não é só descobrir se ela "funciona". Tem objetivos mais profundos.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} className="text-[#00FF85] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Construir Confiança</h3>
                    <p className="text-gray-400 text-sm">Quando você sabe que sua estratégia foi validada com 100+ trades, você confia nela durante drawdowns. Essa confiança impede decisões emocionais.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} className="text-[#00FF85] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Reduzir Emoção</h3>
                    <p className="text-gray-400 text-sm">Com dados concretos, você não precisa "achar" que está certo. Você sabe. Isso reduz a ansiedade e melhora a execução.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} className="text-[#00FF85] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Decisões Baseadas em Dados</h3>
                    <p className="text-gray-400 text-sm">Em vez de operar no achismo, você opera com informação. Isso é o que separa traders amadores de profissionais.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Valide Suas Estratégias com Dados Reais</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam a acompanhar grandes volumes de trades, facilitando a validação de estratégias com mais clareza. Veja win rate, RR, profit factor e consistência — tudo em um lugar.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Validação Leva Tempo — E Vale a Pena</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Validar uma estratégia exige tempo, disciplina e dados suficientes para tomar decisões consistentes. Não existe atalho. Mas o resultado é confiança real no que você está fazendo.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Quantos trades são suficientes?</h3>
                <p className="text-gray-400 text-sm">Depende do contexto, mas como referência: 20-30 para sinais iniciais, 50-100 para conclusões mais sólidas, 100+ para alta confiança. Quanto mais, melhor.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Dá pra confiar em poucos trades?</h3>
                <p className="text-gray-400 text-sm">Não. Com poucos trades, a variância domina. Você pode ter 80% de acerto em 10 trades por pura sorte. Isso não significa que a estratégia funciona.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Backtest substitui trades reais?</h3>
                <p className="text-gray-400 text-sm">Não. Backtest é útil para filtrar ideias ruins, mas não considera slippage, emoção e a pressão de dinheiro real. Validação final sempre deve ser com trades reais.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como validar uma estratégia corretamente?</h3>
                <p className="text-gray-400 text-sm">Execute pelo menos 50-100 trades seguindo as regras à risca. Acompanhe métricas (win rate, RR, profit factor). Faça isso em diferentes condições de mercado. Separe por ativo. Só então tire conclusões.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada.</p>
              </Link>

              <Link
                to="/pt/profit-factor-trading"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Profit Factor no Trading</h3>
                <p className="text-gray-500 text-sm">Entenda a métrica que mostra a qualidade real da estratégia.</p>
              </Link>

              <Link
                to="/pt/como-melhorar-risk-reward"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Melhorar Risk Reward</h3>
                <p className="text-gray-500 text-sm">Técnicas para melhorar sua relação risco/retorno.</p>
              </Link>

              <Link
                to="/pt/win-rate-trading"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Win Rate no Trading</h3>
                <p className="text-gray-500 text-sm">Por que win rate pode enganar se analisado sozinho.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QuantosTradesValidar;
